import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, products, articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'fashion');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const categoryProducts = products.filter((p) => p.categoryId === activeCategory);
  const categoryArticles = articles.filter((a) => a.categoryId === activeCategory);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '分类'} - 亚洲精品`,
            description: currentCategory?.description || '亚洲精品分类页面',
            url: `https://pcnqq.cn/category/${activeCategory}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '亚洲精品',
              url: 'https://pcnqq.cn/',
            },
          }),
        }}
      />

      <main>
        <section className="relative h-52 md:h-72 overflow-hidden">
          <img
            src={currentCategory?.image || categories[0].image}
            alt={`${currentCategory?.name} - 亚洲精品分类`}
            title={`${currentCategory?.name} - ${currentCategory?.description}`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-xl md:text-3xl font-heading font-bold text-white mb-1">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-white/70">{currentCategory?.description}</p>
            </div>
          </div>
        </section>

        <Breadcrumb />

        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 w-4 h-4 inline-flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                {currentCategory?.name}精选
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                共 {categoryProducts.length} 件精品
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop>
              {categoryProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/category/${product.categoryId}`}
                  className="group block bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={product.image}
                      alt={`${product.name} - ${product.brand} | 亚洲精品`}
                      title={`${product.name} - 亚洲精品 ${currentCategory?.name}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {product.tag && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-white text-xs rounded-full whitespace-nowrap">
                        {product.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-foreground-400 mb-1">{product.brand} · {product.origin}</p>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-semibold text-accent-600 whitespace-nowrap">{product.price}</span>
                      <span className="flex items-center gap-0.5 text-xs text-foreground-400">
                        <i className="ri-star-fill w-3 h-3 flex items-center justify-center text-primary-400"></i>
                        {product.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {categoryArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                  相关资讯
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  了解{currentCategory?.name}的更多故事
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={`${article.title} - 亚洲精品资讯`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 text-xs text-foreground-400 mb-2">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime}阅读</span>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}